import {
  CatalogRepository,
  CatalogFilterParams,
  CatalogFilterOptions,
  PaginatedProductsResult,
} from '../../domain/repositories/catalog.repository';
import { ProductEntity, ProductProps } from '../../domain/entities/product.entity';
import { CategoryEntity } from '../../domain/entities/category.entity';
import { BrandEntity } from '../../domain/entities/brand.entity';
import { IHttpClient } from '../../application/interfaces/http-client.interface';
import { resolveMediaUrl } from '../utils/media-url.helper';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  total?: number;
  message?: string;
}

interface BackendSubCategory {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  imageId?: string | null;
  imageUrl?: string | null;
  status?: boolean | string | number;
  displayOrder?: number;
  categoryId?: string;
  productsCount?: number;
}

interface BackendCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  imageUrl?: string;
  displayOrder: number;
  productsCount?: number;
  subCategories?: BackendSubCategory[];
}

interface BackendBrand {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string | null;
  websiteUrl?: string | null;
  displayOrder?: number;
  isFeatured?: boolean;
}

export class HttpCatalogRepository implements CatalogRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  private mapProductProps(item: ProductProps): ProductProps {
    return {
      ...item,
      imageUrl: resolveMediaUrl(item.imageUrl),
      images: item.images ? item.images.map((img) => resolveMediaUrl(img)) : (item.imageUrl ? [resolveMediaUrl(item.imageUrl)] : []),
      datasheetUrl: item.datasheetUrl ? resolveMediaUrl(item.datasheetUrl) : undefined,
    };
  }

  private buildQueryString(params?: CatalogFilterParams): string {
    if (!params) return '';
    const queryParts: string[] = [];

    if (params.categorySlug && params.categorySlug !== 'all') {
      queryParts.push(`category=${encodeURIComponent(params.categorySlug)}`);
    }

    const subSlug = params.subcategorySlug || params.subcategoryId;
    if (subSlug && subSlug !== 'all') {
      queryParts.push(`subcategory=${encodeURIComponent(subSlug)}`);
    }

    if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    if (params.featuredOnly) queryParts.push(`featured=true`);
    if (params.inPromotion) queryParts.push(`inPromotion=true`);
    if (params.all) queryParts.push(`all=true`);

    if (params.brands && params.brands.length > 0) {
      params.brands.forEach((b) => queryParts.push(`brands=${encodeURIComponent(b)}`));
    } else if (params.brand) {
      queryParts.push(`brand=${encodeURIComponent(params.brand)}`);
    }

    if (params.operatingSystems && params.operatingSystems.length > 0) {
      params.operatingSystems.forEach((os) => queryParts.push(`operatingSystems=${encodeURIComponent(os)}`));
    }
    if (params.screenSizes && params.screenSizes.length > 0) {
      params.screenSizes.forEach((s) => queryParts.push(`screenSizes=${encodeURIComponent(s)}`));
    }
    if (params.ramSizes && params.ramSizes.length > 0) {
      params.ramSizes.forEach((r) => queryParts.push(`ramSizes=${encodeURIComponent(r)}`));
    }
    if (params.storageSizes && params.storageSizes.length > 0) {
      params.storageSizes.forEach((st) => queryParts.push(`storageSizes=${encodeURIComponent(st)}`));
    }
    if (params.processors && params.processors.length > 0) {
      params.processors.forEach((p) => queryParts.push(`processors=${encodeURIComponent(p)}`));
    }
    if (params.connectivity && params.connectivity.length > 0) {
      params.connectivity.forEach((c) => queryParts.push(`connectivity=${encodeURIComponent(c)}`));
    }
    if (params.scannerTypes && params.scannerTypes.length > 0) {
      params.scannerTypes.forEach((sc) => queryParts.push(`scannerTypes=${encodeURIComponent(sc)}`));
    }

    if (params.limit !== undefined) {
      queryParts.push(`limit=${params.limit}`);
    }
    if (params.offset !== undefined) {
      queryParts.push(`offset=${params.offset}`);
    } else if (params.page !== undefined && params.limit !== undefined) {
      queryParts.push(`offset=${(params.page - 1) * params.limit}`);
    }

    return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  }

  public async getProducts(params?: CatalogFilterParams): Promise<ProductEntity[]> {
    const queryString = this.buildQueryString(params);
    const res = await this.httpClient.get<ApiResponse<ProductProps[]>>(`/products${queryString}`);

    if (!res.success || !res.data) {
      return [];
    }

    return res.data.map((item) => new ProductEntity(this.mapProductProps(item)));
  }

  public async getProductsPaginated(params?: CatalogFilterParams): Promise<PaginatedProductsResult> {
    const queryString = this.buildQueryString(params);
    const res = await this.httpClient.get<ApiResponse<ProductProps[]>>(`/products${queryString}`);

    if (!res.success || !res.data) {
      return { products: [], total: 0 };
    }

    return {
      products: res.data.map((item) => new ProductEntity(this.mapProductProps(item))),
      total: res.total ?? res.data.length,
    };
  }

  public async getProductBySlug(slug: string): Promise<ProductEntity | null> {
    try {
      const res = await this.httpClient.get<ApiResponse<ProductProps>>(`/products/slug/${encodeURIComponent(slug)}`);
      if (!res.success || !res.data) {
        return null;
      }
      return new ProductEntity(this.mapProductProps(res.data));
    } catch {
      return null;
    }
  }

  public async getCategories(): Promise<CategoryEntity[]> {
    const res = await this.httpClient.get<ApiResponse<BackendCategory[]>>('/categories?includeSubcategories=true');
    if (!res.success || !res.data) {
      return [];
    }

    return res.data.map(
      (cat) =>
        new CategoryEntity({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          description: cat.description || '',
          itemCount: cat.productsCount ?? 0,
          imageUrl: cat.imageUrl ? resolveMediaUrl(cat.imageUrl) : undefined,
          subcategories: (cat.subCategories || []).map((sub) => ({
            id: sub.id,
            name: sub.name,
            slug: sub.slug,
            description: sub.description,
            imageId: sub.imageId,
            imageUrl: sub.imageUrl ? resolveMediaUrl(sub.imageUrl) : undefined,
            status: 'active',
            displayOrder: sub.displayOrder,
            categoryId: sub.categoryId || cat.id,
            categoryName: cat.name,
            categorySlug: cat.slug,
            itemCount: sub.productsCount ?? 0,
          })),
        })
    );
  }

  public async getBrands(): Promise<string[]> {
    const res = await this.httpClient.get<ApiResponse<BackendBrand[]>>('/brands');
    if (!res.success || !res.data) {
      return [];
    }
    return res.data.map((b) => b.name);
  }

  public async getBrandEntities(): Promise<BrandEntity[]> {
    const res = await this.httpClient.get<ApiResponse<BackendBrand[]>>('/brands');
    if (!res.success || !res.data) {
      return [];
    }
    return res.data.map(
      (b) =>
        new BrandEntity({
          id: b.id,
          name: b.name,
          slug: b.slug,
          logoUrl: b.logoUrl ? resolveMediaUrl(b.logoUrl) : null,
          websiteUrl: b.websiteUrl,
          displayOrder: b.displayOrder ?? 0,
          isFeatured: b.isFeatured ?? false,
        })
    );
  }

  public async getFilterOptions(categorySlug?: string): Promise<CatalogFilterOptions> {
    const query = categorySlug ? `?category=${encodeURIComponent(categorySlug)}` : '';
    const res = await this.httpClient.get<ApiResponse<CatalogFilterOptions>>(`/products/filter-options${query}`);
    if (!res.success || !res.data) {
      return {
        subcategories: [],
        brands: [],
        operatingSystems: [],
        screenSizes: [],
        ramSizes: [],
        storageSizes: [],
        processors: [],
        connectivity: [],
        scannerTypes: [],
      };
    }
    return res.data;
  }
}
